import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from '../auth';
import { useToast } from "@/hooks/use-toast";
import { toActivityTypeColumns } from "@/lib/activityTypeKey";
import { maybeOpenScheduleAfterActivityMarkedDone } from "@/lib/activitySchedulePrompt";
import { useIsInFocusedExecution } from "@/contexts/FocusedExecutionContext";
import { deleteGoogleEventForActivity } from "./deleteGoogleEventForActivity";
import { useOrgHasMultiDealMeetings } from "./useOrgHasMultiDealMeetings";

export interface Activity {
  id: string;
  org_id: string;
  title: string;
  description?: string;
  type: string;
  lead_id?: string;
  company_id?: string;
  deal_id?: string;
  email_message_id?: string | null;
  owner_id?: string;
  date: string;
  duration_minutes?: number;
  status: string;
  outcome?: 'won' | 'lost' | 'pending';
  loss_reason_id?: string;
  custom_loss_reason?: string;
  custom_data?: Record<string, any>;
  subtasks?: Array<{
    title: string;
    status: 'pending' | 'in_progress' | 'done';
    date?: string; // ISO date for scheduling this subtask
    expected_time_minutes?: number;
    completed_at?: string;
  }>;
  notes?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  activity_type_key?: string;
  sync_to_calendar?: boolean | null;
  google_event_id?: string | null;
  location?: string | null;
  leads?: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    source?: string;
    company_id?: string | null;
    companies?: {
      id: string;
      name: string;
      website?: string | null;
      industry?: string | null;
    } | null;
  };
  companies?: {
    id: string;
    name: string;
    website?: string | null;
    industry?: string | null;
  };
  deals?: {
    id: string;
    title: string;
  };
  owner?: {
    user_id: string;
    email: string;
    full_name: string;
  };
}

export interface UseActivitiesParams {
  statuses?: string[];
  types?: string[];
  ownerIds?: string[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
  filterId?: string | null;
  /** Coluna de ordenação. Whitelist server-side: date | time | title | type | status | created_at | updated_at. */
  sortBy?: string;
  /** Direção. Default `desc`. */
  sortOrder?: 'asc' | 'desc';
  /**
   * Quando true, oculta atividades originadas de sincronização com o Google
   * Calendar (custom_data.source = 'calendar_sync'). Usado nas páginas
   * /activities (lista) e /activities/focus para não poluir a execução com
   * reuniões importadas da agenda.
   */
  excludeCalendarSync?: boolean;
}

const DEFAULT_PAGE_SIZE = 50;

export const useActivities = (filterIdOrParams?: string | null | UseActivitiesParams) => {
  const { orgId } = useCurrentOrgId();

  // Normalize params: support legacy `filterId` string OR new params object
  const params: UseActivitiesParams = typeof filterIdOrParams === 'object' && filterIdOrParams !== null
    ? filterIdOrParams
    : ({ filterId: filterIdOrParams ?? undefined } as UseActivitiesParams);

  const {
    statuses,
    types,
    ownerIds,
    search,
    dateFrom,
    dateTo,
    page,
    pageSize,
    filterId,
    excludeCalendarSync,
    sortBy,
    sortOrder,
  } = params;

  return useQuery({
    queryKey: ["activities", orgId, params],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization ID not found");

      const effectivePage = page && page > 0 ? page : 1;
      const effectivePageSize = pageSize && pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE;
      const pOffset = (effectivePage - 1) * effectivePageSize;
      const hasNoOwner = ownerIds?.includes("__no_owner__") ?? false;
      const realOwnerIds = ownerIds?.filter(id => id !== "__no_owner__") ?? [];

      const { data: rpcData, error: rpcError } = await supabase.rpc("get_activities_page", {
        p_org_id: orgId,
        p_offset: pOffset,
        p_limit: effectivePageSize,
        p_date_from: dateFrom || null,
        p_date_to: dateTo || null,
        p_statuses: statuses?.length ? statuses : null,
        p_types: types?.length ? types : null,
        p_owner_ids: realOwnerIds.length > 0 ? realOwnerIds : null,
        p_include_no_owner: hasNoOwner,
        p_search: search?.trim() || null,
        p_exclude_calendar_sync: excludeCalendarSync ?? false,
        p_filter_id: filterId || null,
        p_sort_by: sortBy ?? 'date',
        p_sort_order: sortOrder ?? 'desc',
      });
      if (rpcError) throw rpcError;
      let activities = (Array.isArray(rpcData) ? rpcData : []) as any[];

      // Fetch owner data separately for activities that have owner_id
      const activitiesWithOwners = activities.filter(activity => activity.owner_id);
      if (activitiesWithOwners.length > 0) {
        const ownerIdsToFetch = [...new Set(activitiesWithOwners.map(a => a.owner_id))];
        const { data: ownersData, error: ownersError } = await supabase
          .from('profiles')
          .select('user_id, email, full_name')
          .in('user_id', ownerIdsToFetch);
        if (!ownersError && ownersData) {
          activities = activities.map(activity => ({
            ...activity,
            owner: activity.owner_id ? ownersData.find(o => o.user_id === activity.owner_id) : null
          }));
        }
      }

      return activities;
    },
    enabled: !!orgId,
    // Query pesada (RPC get_activities_page + embeds). A lista já recebe frescor
    // via useCrmRealtime na rota /activities; matar o refetch-on-focus/reconnect
    // evita disparar a query mais cara do banco a cada alt-tab.
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Lightweight hook for activity KPI stats (counts by status).
 * Uses the get_activities_count RPC so saved-filter rules (p_filter_id) are
 * applied server-side and the totals match the table view.
 */
export const useActivityStats = (params?: {
  statuses?: string[];
  types?: string[];
  ownerIds?: string[];
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  excludeCalendarSync?: boolean;
  filterId?: string | null;
}) => {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ["activity-stats", orgId, params],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization ID not found");

      const hasNoOwner = params?.ownerIds?.includes("__no_owner__") ?? false;
      const realOwnerIds = params?.ownerIds?.filter(id => id !== "__no_owner__") ?? [];

      const callCount = async (statusFilter?: string, dateOverride?: { from?: string | null; to?: string | null }) => {
        const { data, error } = await supabase.rpc("get_activities_count", {
          p_org_id: orgId,
          p_date_from: dateOverride?.from ?? params?.dateFrom ?? null,
          p_date_to: dateOverride?.to ?? params?.dateTo ?? null,
          p_statuses: statusFilter ? [statusFilter] : (params?.statuses?.length ? params.statuses : null),
          p_types: params?.types?.length ? params.types : null,
          p_owner_ids: realOwnerIds.length > 0 ? realOwnerIds : null,
          p_include_no_owner: hasNoOwner,
          p_search: params?.search?.trim() || null,
          p_exclude_calendar_sync: params?.excludeCalendarSync ?? false,
          p_filter_id: params?.filterId || null,
        });
        if (error) throw error;
        return Number(data ?? 0);
      };

      // Start of today for overdue split
      const now = new Date();
      const startOfTodayISO = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

      const [total, completed, pendingToday, overdue, paused] = await Promise.all([
        callCount(),
        callCount("done"),
        callCount("pending", { from: startOfTodayISO, to: params?.dateTo ?? null }),
        callCount("pending", { from: params?.dateFrom ?? null, to: startOfTodayISO }),
        callCount("paused"),
      ]);

      return {
        total,
        completed,
        pending: pendingToday,
        overdue,
        pendingOrOverdue: pendingToday + overdue,
        paused,
      };
    },
    enabled: !!orgId,
    // 5 RPCs get_activities_count por load; mesma lógica de frescor da lista.
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

// Helper function to check if an activity meets a filter rule
function checkActivityRule(activity: any, rule: any): boolean {
  const field = rule.campo_filtrado;
  const operator = rule.operador;
  const value = rule.valor;

  // Get the actual value from the activity
  let activityValue: any;
  
  if (field.includes('.')) {
    const [entity, prop] = field.split('.');
    if (entity === 'activities') {
      activityValue = activity[prop];
    } else if (entity === 'leads' && activity.leads) {
      activityValue = activity.leads[prop];
    } else if (entity === 'companies' && activity.companies) {
      activityValue = activity.companies[prop];
    }
  } else {
    activityValue = activity[field];
  }

  // Special handling for date fields
  if (field === 'activities.date' && activityValue) {
    const activityDate = new Date(activityValue);
    
    // Compare only the date part (without time)
    const activityDateOnly = new Date(activityDate.getFullYear(), activityDate.getMonth(), activityDate.getDate());
    
    switch (operator) {
      case 'equals':
        const ruleDate = new Date(value);
        const ruleDateOnly = new Date(ruleDate.getFullYear(), ruleDate.getMonth(), ruleDate.getDate());
        return activityDateOnly.getTime() === ruleDateOnly.getTime();
        
      case 'not_equals':
        const ruleDateNot = new Date(value);
        const ruleDateOnlyNot = new Date(ruleDateNot.getFullYear(), ruleDateNot.getMonth(), ruleDateNot.getDate());
        return activityDateOnly.getTime() !== ruleDateOnlyNot.getTime();
        
      case 'after':
        const afterDate = new Date(value);
        const afterDateOnly = new Date(afterDate.getFullYear(), afterDate.getMonth(), afterDate.getDate());
        return activityDateOnly > afterDateOnly;
        
      case 'before':
        const beforeDate = new Date(value);
        const beforeDateOnly = new Date(beforeDate.getFullYear(), beforeDate.getMonth(), beforeDate.getDate());
        return activityDateOnly < beforeDateOnly;
        
      case 'between':
        const [startDateStr, endDateStr] = value.split('|');
        if (!startDateStr || !endDateStr) return false;
        
        const startDate = new Date(startDateStr);
        const endDate = new Date(endDateStr);
        const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        
        return activityDateOnly >= startDateOnly && activityDateOnly <= endDateOnly;
        
      case 'is_empty':
        return !activityValue || activityValue === '';
        
      case 'is_not_empty':
        return !!activityValue && activityValue !== '';
        
      default:
        return true;
    }
  }

  // Convert to string for comparison (except for numeric comparisons)
  const activityValueStr = activityValue?.toString().toLowerCase() || '';
  const ruleValueStr = value?.toString().toLowerCase() || '';

  switch (operator) {
    case 'equals':
      return activityValueStr === ruleValueStr;
    case 'not_equals':
      return activityValueStr !== ruleValueStr;
    case 'contains':
      return activityValueStr.includes(ruleValueStr);
    case 'not_contains':
      return !activityValueStr.includes(ruleValueStr);
    case 'greater_than':
      return parseFloat(activityValue) > parseFloat(value);
    case 'less_than':
      return parseFloat(activityValue) < parseFloat(value);
    case 'is_empty':
      return !activityValue || activityValue === '';
    case 'is_not_empty':
      return !!activityValue && activityValue !== '';
    default:
      return true;
  }
}

/**
 * Teto por query da timeline do deal. Evita estourar o cap de 1000 linhas do
 * PostgREST em deals com histórico muito longo (que retornava 501/502) e enxuga
 * o payload. Como as 3 queries ordenam por `date desc`, o corte mantém as
 * atividades mais recentes — as relevantes para timeline e painéis.
 */
const MAX_ACTIVITIES_PER_DEAL = 200;

export const useActivitiesByDeal = (dealId: string | undefined, leadId?: string | null) => {
  const { orgId } = useCurrentOrgId();
  const { data: multiDealFlag } = useOrgHasMultiDealMeetings();
  // `=== true` de propósito: enquanto a flag carrega (undefined) tratamos como
  // false, então undefined e false compartilham a MESMA queryKey — as 158 orgs
  // sem multi-deal não pagam um refetch extra quando a flag resolve. Só a org
  // que de fato usa o recurso muda de chave e refaz a busca incluindo a reunião.
  const hasMultiDealMeetings = multiDealFlag === true;

  return useQuery({
    queryKey: ["activities", "deal", dealId, leadId, orgId, hasMultiDealMeetings],
    queryFn: async () => {
      if (!orgId || !dealId) throw new Error("Organization ID or Deal ID not found");

      // Atividades vinculadas ao negócio. Embeds enxutos de propósito: timeline e
      // painéis usam apenas companies.name (compose de e-mail / modal de edição) e
      // deals.title — não custom_data nem os 20+ campos de companies.
      const { data: dealActivities, error: dealError } = await supabase
        .from("activities")
        .select(`
          *,
          leads(id, name, email, phone, source),
          companies(id, name),
          deals(id, title)
        `)
        .eq("org_id", orgId)
        .eq("deal_id", dealId)
        .order("date", { ascending: false })
        .limit(MAX_ACTIVITIES_PER_DEAL);

      if (dealError) throw dealError;

      let allActivities = (dealActivities as any[]) || [];

      // Se houver lead relacionado, buscar também atividades do lead
      if (leadId) {
        const { data: leadActivities, error: leadError } = await supabase
          .from("activities")
          .select(`
            *,
            leads(id, name, email, phone, source),
            companies(id, name),
            deals(id, title)
          `)
          .eq("org_id", orgId)
          .eq("lead_id", leadId)
          .order("date", { ascending: false })
          .limit(MAX_ACTIVITIES_PER_DEAL);

        if (leadError) {
          console.error('❌ [useActivitiesByDeal] Erro ao buscar atividades do lead:', leadError);
        } else {
          // Combinar atividades, removendo duplicatas (caso uma atividade tenha tanto deal_id quanto lead_id)
          const existingIds = new Set(allActivities.map(a => a.id));
          const uniqueLeadActivities = (leadActivities as any[] || []).filter(a => !existingIds.has(a.id));
          allActivities = [...allActivities, ...uniqueLeadActivities];
          
          // Ordenar por data novamente após combinar
          allActivities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        }
      }

      // Multi-deal ("vincular a ambos"): reuniões auto-vinculadas a vários deals
      // carregam custom_data.matched_deals. Este deal pode ser um dos SECUNDÁRIOS
      // (deal_id/lead_id não apontam para ele) — busca por containment jsonb p/
      // exibir a reunião também aqui. Dedup por id contra o que já veio acima.
      //
      // GATEADA por `organizations.has_multi_deal_meetings` (auto-mantida pelo
      // trigger trg_mark_org_has_multi_deal_meetings) — o gate protege as 90 orgs
      // que não usam o recurso.
      //
      // VIA RPC, NÃO VIA PostgREST — não volte a chamar `.from("activities")
      // .contains("custom_data", ...)` aqui. Sob RLS o planner ABANDONA o índice
      // GIN: a policy `activities_select` (org_id IN (SELECT ... FROM memberships))
      // faz o índice de org_id parecer servir aos dois predicados de org, e o
      // `@>` vira filtro opaco sob generic plan. Medido em prod (03/08) com
      // SET ROLE authenticated + force_generic_plan:
      //
      //   PostgREST/RLS : 166.052 linhas varridas, 163.998 descartadas por filtro,
      //                   36.550 buffers, 389ms  (4.375ms sob concorrência real)
      //   RPC SecDef    : BitmapAnd com o GIN, 3.521 buffers, 18,8ms
      //
      // Isso derrubava ~175s de tempo de banco a cada 25s (≈7x num box de 4 vCPU)
      // e causava rajadas de statement timeout em TODA a aplicação. Índice parcial
      // foi testado e NÃO resolve — o planner continua ignorando sob RLS. O que
      // resolve é tirar o predicado de RLS do scan, que é o que a RPC faz
      // (segurança preservada por is_member() + escopo de org_id lá dentro).
      // Ver incidente de performance 03/08 e a migration da RPC.
      const matchedDealActivities = hasMultiDealMeetings
        ? await (async () => {
            const { data, error } = await (supabase.rpc as any)(
              "rpc_activities_by_matched_deal",
              {
                p_org_id: orgId,
                p_deal_id: dealId,
                p_limit: MAX_ACTIVITIES_PER_DEAL,
              }
            );
            if (error) {
              console.error('❌ [useActivitiesByDeal] Erro ao buscar atividades multi-deal:', error);
              return null;
            }
            // A RPC devolve um array jsonb no MESMO formato dos embeds do
            // PostgREST (activities.* + leads/companies/deals aninhados).
            return (data as any[] | null) ?? null;
          })()
        : null;

      if (matchedDealActivities?.length) {
        const existingIds = new Set(allActivities.map(a => a.id));
        const uniqueMatched = matchedDealActivities.filter(a => !existingIds.has(a.id));
        if (uniqueMatched.length) {
          allActivities = [...allActivities, ...uniqueMatched];
          allActivities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        }
      }

      // Perfis (owner_id + created_by) em UMA query — antes eram duas idas ao
      // /profiles pela mesma tabela. `owner` alimenta o responsável da atividade
      // (timeline/execução); `profile`, o criador (notas/eventos de sistema).
      const profileIds = [...new Set(
        allActivities
          .flatMap(a => [a.owner_id, a.created_by])
          .filter(Boolean)
      )];

      if (profileIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, email, full_name')
          .in('user_id', profileIds);

        if (profilesError) {
          console.error('❌ [useActivitiesByDeal] Erro ao buscar perfis (owner/criador):', profilesError);
        } else {
          const byId = new Map((profilesData ?? []).map(p => [p.user_id, p]));
          allActivities = allActivities.map(activity => ({
            ...activity,
            owner: activity.owner_id ? byId.get(activity.owner_id) ?? null : null,
            profile: activity.created_by ? byId.get(activity.created_by) ?? null : null,
          }));
        }
      }

      return allActivities;
    },
    enabled: !!orgId && !!dealId,
    // Mesmo tratamento de `useActivities` (acima): são 3 SELECTs em `activities`
    // com embeds por execução. No default `crm` (30s + refetchOnWindowFocus) cada
    // alt-tab do usuário redisparava as três — e o /deals/:id fica aberto o dia
    // todo. Junto com a invalidação realtime por prefixo em useCrmRealtime, era o
    // amplificador que levava a query multi-deal a 40+ chamadas por 25s vindas de
    // uma única org. Ver incidente de performance 03/08.
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Desfaz um auto-vínculo de reunião → negócio (RPC unlink_autolinked_meeting).
 * Zera deal/lead/company + matched_deals da atividade (a reunião continua na
 * agenda, só sai das timelines de deal). Gate server-side por can_manage_agenda.
 */
export const useUnlinkAutolinkedMeeting = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('unlink_autolinked_meeting' as never, {
        p_activity_id: activityId,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      toast({ title: 'Reunião desvinculada do negócio' });
    },
    onError: (error: Error) => {
      toast({ title: 'Não foi possível desvincular', description: error.message, variant: 'destructive' });
    },
  });
};

export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (activityData: any) => {
      console.log('🔍 [useCreateActivity] Received activityData:', activityData);
      if (!orgId) throw new Error("Organization ID not found");

      // Simplificar insertData para usar apenas campos que existem no banco
      const typeKey = String(activityData.activity_type_key || activityData.type || "").trim();
      const cols = toActivityTypeColumns(typeKey || "task");

      // Notes are comments, not tasks — they cannot be pending/paused/canceled.
      const isNote = cols.type === "note";
      const resolvedStatus = isNote ? "done" : (activityData.status || "pending");

      const insertData: any = {
        type: cols.type,
        activity_type_key: cols.activity_type_key,
        title: activityData.title,
        description: activityData.description || null,
        date: activityData.date,
        status: resolvedStatus,
        lead_id: activityData.lead_id || null,
        company_id: activityData.company_id || null,
        created_by: activityData.created_by,
        org_id: orgId,
      };

      // Adicionar campos opcionais apenas se existirem
      if (activityData.duration_minutes !== undefined) {
        insertData.duration_minutes = activityData.duration_minutes;
      }
      if (activityData.deal_id) {
        insertData.deal_id = activityData.deal_id;
      }
      // ⚠️ `insertData` é WHITELIST, não spread: campo que não estiver listado aqui é
      // descartado em silêncio, sem erro. Foi o que aconteceu com `contract_id` na primeira
      // tentativa — a reunião nascia solta, sem vínculo com o contrato, e a timeline do
      // contrato continuava vazia como se nada tivesse sido criado.
      if (activityData.contract_id) {
        insertData.contract_id = activityData.contract_id;
      }
      if (activityData.email_message_id) {
        insertData.email_message_id = activityData.email_message_id;
      }
      if (activityData.owner_id) {
        insertData.owner_id = activityData.owner_id;
      }
      if (activityData.meeting_type_id) {
        insertData.meeting_type_id = activityData.meeting_type_id;
      }
      if (activityData.custom_data) {
        insertData.custom_data = activityData.custom_data;
      }
      if (activityData.subtasks) {
        insertData.subtasks = activityData.subtasks;
      }

      // Validate required fields
      if (!insertData.type || !insertData.activity_type_key) {
        throw new Error("Tipo da atividade é obrigatório");
      }
      if (!insertData.title) {
        throw new Error("Título da atividade é obrigatório");
      }
      if (!insertData.date) {
        throw new Error("Data da atividade é obrigatória");
      }
      if (!insertData.created_by) {
        throw new Error("Usuário criador é obrigatório");
      }
      if (!insertData.org_id) {
        throw new Error("ID da organização é obrigatório");
      }

      console.log('🔍 [useCreateActivity] Insert data:', insertData);

      const { data, error } = await supabase
        .from("activities")
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('❌ [useCreateActivity] Supabase error:', error);
        throw error;
      }
      console.log('✅ [useCreateActivity] Activity created successfully:', data);
      return data;
    },
    onSuccess: async (createdActivity) => {
      console.log('🔍 [useCreateActivity] Atividade criada:', createdActivity);
      console.log('🔍 [useCreateActivity] Status da atividade:', createdActivity.status);
      console.log('🔍 [useCreateActivity] Lead ID:', createdActivity.lead_id);
      
      // Se a atividade foi criada como concluída e tem um lead associado
      if (createdActivity.status === 'done' && createdActivity.lead_id) {
        console.log('🔄 [useCreateActivity] Processando atualização de status do lead...');
        try {
          // Buscar o lead atual para verificar o status
          const { data: leadData, error: leadError } = await supabase
            .from('leads')
            .select('id, status')
            .eq('id', createdActivity.lead_id)
            .single();

          if (leadError) {
            console.error('❌ [useCreateActivity] Erro ao buscar lead:', leadError);
          } else {
            console.log('📋 [useCreateActivity] Lead encontrado:', leadData);
            console.log('📋 [useCreateActivity] Status atual do lead:', leadData.status);
            
            if (leadData && leadData.status === 'new') {
              console.log('🔄 [useCreateActivity] Atualizando status do lead de "new" para "contacted"...');
              
              // Atualizar status do lead para 'contacted' se ainda estiver como 'new'
              const { error: updateLeadError } = await supabase
                .from('leads')
                .update({ 
                  status: 'contacted',
                  updated_at: new Date().toISOString()
                })
                .eq('id', createdActivity.lead_id);

              if (updateLeadError) {
                console.error('❌ [useCreateActivity] Erro ao atualizar status do lead:', updateLeadError);
              } else {
                console.log('✅ [useCreateActivity] Status do lead atualizado para "Contatado" (atividade criada como concluída)');
                // Invalidar queries relacionadas ao lead
                queryClient.invalidateQueries({ queryKey: ['leads'] });
                queryClient.invalidateQueries({ queryKey: ['lead'] });
              }
            } else {
              console.log('ℹ️ [useCreateActivity] Lead não está com status "new", não será atualizado. Status atual:', leadData.status);
            }
          }
        } catch (error) {
          console.error('❌ [useCreateActivity] Erro ao processar atualização de status do lead:', error);
        }
      } else {
        console.log('ℹ️ [useCreateActivity] Atividade não foi criada como concluída ou não tem lead associado');
      }

      // ["activities"] já é prefixo de ["activities", orgId] e ["activities",
      // orgId, null] — invalidar só o prefixo cobre todas as variações (evita
      // 3 refetches sobrepostos da query pesada por mutation).
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ['supervisao', 'operacao-reps'] });
      toast({
        title: "Atividade criada",
        description: "A atividade foi criada com sucesso.",
      });
    },
    onError: (error) => {
      console.error("Error creating activity:", error);
      toast({
        title: "Erro ao criar atividade",
        description: "Ocorreu um erro ao criar a atividade. Tente novamente.",
        variant: "destructive",
      });
    },
  });
};

// Função auxiliar para cancelar atividades não concluídas de um lead
const cancelOtherActivitiesForLead = async (leadId: string, excludeActivityId: string) => {
  try {
    console.log('🔄 [cancelOtherActivitiesForLead] Canceling other activities for lead:', leadId);
    
    const { error } = await supabase
      .from('activities')
      .update({
        status: 'canceled',
        updated_at: new Date().toISOString()
      })
      .eq('lead_id', leadId)
      .eq('status', 'pending')
      .neq('id', excludeActivityId);

    if (error) {
      console.error('❌ [cancelOtherActivitiesForLead] Error canceling activities:', error);
      throw error;
    }

    console.log('✅ [cancelOtherActivitiesForLead] Other activities canceled successfully');
  } catch (error) {
    console.error('❌ [cancelOtherActivitiesForLead] Error:', error);
    throw error;
  }
};

export const useUpdateActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();
  const isInFocusedExecution = useIsInFocusedExecution();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data: prevRow, error: prevErr } = await supabase
        .from("activities")
        .select("id, status, org_id, deal_id, lead_id, company_id, owner_id, type, activity_type_key")
        .eq("id", id)
        .maybeSingle();
      if (prevErr) throw prevErr;

      const patch: Record<string, unknown> = { ...updates };
      if (patch.type !== undefined && patch.activity_type_key === undefined) {
        const cols = toActivityTypeColumns(String(patch.type));
        patch.type = cols.type;
        patch.activity_type_key = cols.activity_type_key;
      }

      // Notes are comments — force status='done' to satisfy the DB invariant.
      const effectiveType = (patch.type as string | undefined) ?? prevRow?.type;
      if (effectiveType === "note" && patch.status !== undefined && patch.status !== "done") {
        patch.status = "done";
      }

      const { data, error } = await supabase
        .from("activities")
        .update(patch as any)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const out = data as any;
      out.__previousStatus = prevRow?.status ?? null;
      return out;
    },
    onSuccess: async (updatedActivity: any) => {
      const previousStatus = updatedActivity.__previousStatus as string | null | undefined;
      if ("__previousStatus" in updatedActivity) {
        delete updatedActivity.__previousStatus;
      }
      // Ligação concluída não dispara mais webhook do n8n daqui: a intenção de discagem
      // (call_dial_intents, gravada por useCallProvider) + sonax-webhook já vinculam a
      // ligação à atividade.

      // Se a atividade foi marcada como concluída com outcome 'won' ou 'lost'
      if (updatedActivity.status === 'done' && 
          (updatedActivity.outcome === 'won' || updatedActivity.outcome === 'lost')) {
        
        // ============================================
        // CASO 1: Atividade com deal_id - Mover deal na pipeline e cancelar cadência
        // ============================================
        if (updatedActivity.deal_id) {
          try {
            console.log(`🔄 [useUpdateActivity] Atividade com deal: movendo deal ${updatedActivity.deal_id} para ${updatedActivity.outcome}`);

            // Chamar RPC para mover o deal para WON/LOST (cancela cadência via trigger)
            const { data: moveResult, error: moveError } = await supabase.rpc(
              'move_deal_by_activity_outcome',
              {
                p_deal_id: updatedActivity.deal_id,
                p_outcome: updatedActivity.outcome,
                p_loss_reason_id: updatedActivity.loss_reason_id || null,
                p_custom_loss_reason: updatedActivity.custom_loss_reason || null
              }
            );

            if (moveError) {
              console.error('❌ Erro ao mover deal:', moveError);
            } else if (moveResult && (moveResult as { success?: boolean }).success) {
              console.log(`✅ Deal ${updatedActivity.deal_id} movido para ${updatedActivity.outcome}:`, moveResult);
              // Invalidar queries de deals
              queryClient.invalidateQueries({ queryKey: ['deals'] });
              queryClient.invalidateQueries({ queryKey: ['deal'] });
              queryClient.invalidateQueries({ queryKey: ['deal-sequence-schedule'] });
            } else {
              console.warn('⚠️ Resultado do move_deal_by_activity_outcome:', moveResult);
            }

            // Se a atividade também tem lead, atualizar o status do lead
            if (updatedActivity.lead_id) {
              const newLeadStatus = updatedActivity.outcome === 'won' ? 'won' : 'lost';
              
              const updateLeadData: any = {
                status: newLeadStatus,
                updated_at: new Date().toISOString()
              };

              if (updatedActivity.outcome === 'lost') {
                if (updatedActivity.loss_reason_id) {
                  updateLeadData.loss_reason_id = updatedActivity.loss_reason_id;
                }
                if (updatedActivity.custom_loss_reason) {
                  updateLeadData.custom_loss_reason = updatedActivity.custom_loss_reason;
                }
              }

              await supabase
                .from('leads')
                .update(updateLeadData)
                .eq('id', updatedActivity.lead_id);

              console.log(`✅ Status do lead atualizado para "${newLeadStatus}"`);
              queryClient.invalidateQueries({ queryKey: ['leads'] });
              queryClient.invalidateQueries({ queryKey: ['lead'] });
            }

            // Cancelar outras atividades pendentes do deal
            if (updatedActivity.deal_id) {
              const { error: cancelError } = await supabase
                .from('activities')
                .update({ status: 'canceled', updated_at: new Date().toISOString() })
                .eq('deal_id', updatedActivity.deal_id)
                .eq('status', 'pending')
                .neq('id', updatedActivity.id);

              if (cancelError) {
                console.error('Erro ao cancelar atividades do deal:', cancelError);
              } else {
                console.log('✅ Outras atividades pendentes do deal canceladas');
              }
            }
          } catch (error) {
            console.error('Erro ao processar deal outcome:', error);
          }
        }
        // ============================================
        // CASO 2: Atividade apenas com lead (sem deal) - Comportamento atual
        // ============================================
        else if (updatedActivity.lead_id) {
          try {
            // Cancelar outras atividades não concluídas do mesmo lead
            await cancelOtherActivitiesForLead(updatedActivity.lead_id, updatedActivity.id);
            
            // Buscar o lead atual para verificar o status
            const { data: leadData, error: leadError } = await supabase
              .from('leads')
              .select('id, status')
              .eq('id', updatedActivity.lead_id)
              .single();

            if (leadError) {
              console.error('Erro ao buscar lead:', leadError);
            } else if (leadData) {
              // Atualizar status do lead baseado no outcome da atividade
              let newLeadStatus = 'contacted'; // padrão
              
              if (updatedActivity.outcome === 'won') {
                newLeadStatus = 'qualified';
              } else if (updatedActivity.outcome === 'lost') {
                newLeadStatus = 'lost';
              }
              
              // Só atualizar se o lead ainda não estiver em um status mais avançado
              const statusHierarchy = ['new', 'contacted', 'qualified', 'meeting', 'proposal', 'won', 'lost'];
              const currentStatusIndex = statusHierarchy.indexOf(leadData.status);
              const newStatusIndex = statusHierarchy.indexOf(newLeadStatus);
              
              if (newStatusIndex > currentStatusIndex) {
                const updateData: any = { 
                  status: newLeadStatus,
                  updated_at: new Date().toISOString()
                };

                // Incluir motivo de perda de LEAD (colunas agora existem no banco)
                if (updatedActivity.outcome === 'lost') {
                  if (updatedActivity.loss_reason_id) {
                    updateData.loss_reason_id = updatedActivity.loss_reason_id;
                  }
                  if (updatedActivity.custom_loss_reason) {
                    updateData.custom_loss_reason = updatedActivity.custom_loss_reason;
                  }
                }

                const { error: updateLeadError } = await supabase
                  .from('leads')
                  .update(updateData)
                  .eq('id', updatedActivity.lead_id);

                if (updateLeadError) {
                  console.error('Erro ao atualizar status do lead:', updateLeadError);
                } else {
                  console.log(`✅ Status do lead atualizado para "${newLeadStatus}"`);
                  // Invalidar queries relacionadas ao lead
                  queryClient.invalidateQueries({ queryKey: ['leads'] });
                  queryClient.invalidateQueries({ queryKey: ['lead'] });
                }
              }
            }
          } catch (error) {
            console.error('Erro ao processar atualização de status do lead:', error);
          }
        }
      } else if (updatedActivity.status === 'done' && updatedActivity.lead_id) {
        // Lógica original para atividades concluídas sem outcome específico
        try {
          // Buscar o lead atual para verificar o status
          const { data: leadData, error: leadError } = await supabase
            .from('leads')
            .select('id, status')
            .eq('id', updatedActivity.lead_id)
            .single();

          if (leadError) {
            console.error('Erro ao buscar lead:', leadError);
          } else if (leadData && leadData.status === 'new') {
            // Atualizar status do lead para 'contacted' se ainda estiver como 'new'
            const { error: updateLeadError } = await supabase
              .from('leads')
              .update({
                status: 'contacted',
                updated_at: new Date().toISOString()
              })
              .eq('id', updatedActivity.lead_id);

            if (updateLeadError) {
              console.error('Erro ao atualizar status do lead:', updateLeadError);
            } else {
              console.log('✅ Status do lead atualizado para "Contatado"');
              // Invalidar queries relacionadas ao lead
              queryClient.invalidateQueries({ queryKey: ['leads'] });
              queryClient.invalidateQueries({ queryKey: ['lead'] });
            }
          }
        } catch (error) {
          console.error('Erro ao processar atualização de status do lead:', error);
        }
      }

      if (
        orgId &&
        updatedActivity?.status === "done" &&
        previousStatus !== "done"
      ) {
        try {
          await maybeOpenScheduleAfterActivityMarkedDone({
            orgId,
            previousStatus,
            activity: {
              id: updatedActivity.id,
              org_id: updatedActivity.org_id,
              deal_id: updatedActivity.deal_id,
              lead_id: updatedActivity.lead_id,
              company_id: updatedActivity.company_id,
              owner_id: updatedActivity.owner_id,
            },
          });
        } catch (e) {
          console.warn("[useUpdateActivity] schedule prompt", e);
        }
      }

      // ["activities"] já é prefixo das variantes por orgId — invalidar só o
      // prefixo cobre todas (evita 3 refetches sobrepostos por update).
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["lead-activities"] });
      queryClient.invalidateQueries({ queryKey: ['supervisao', 'operacao-reps'] });
      // Focus mode queue listens on a separate key — invalidate so a Concluir
      // gesture immediately advances pending[0] to the next item.
      queryClient.invalidateQueries({ queryKey: ["focus-queue"] });

      if (!isInFocusedExecution) {
        toast({
          title: "Atividade atualizada",
          description: "A atividade foi atualizada com sucesso.",
        });
      }
    },
    onError: (error) => {
      console.error("Error updating activity:", error);
      toast({
        title: "Erro ao atualizar atividade",
        description: "Ocorreu um erro ao atualizar a atividade. Tente novamente.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: existing } = await supabase
        .from("activities")
        .select("id, google_event_id")
        .eq("id", id)
        .maybeSingle();

      const outcome = await deleteGoogleEventForActivity({
        activityId: id,
        googleEventId: existing?.google_event_id ?? null,
        invokeCalendarSync: (body) =>
          supabase.functions.invoke("calendar-sync", { body }) as Promise<any>,
        onWarning: (reason) => {
          console.warn("[useDeleteActivity] Google delete warning:", reason);
        },
      });

      if (outcome.kind === "warned") {
        toast({
          title: "Evento da agenda pode não ter sido removido",
          description:
            "A atividade foi excluída no CRM, mas o evento Google Calendar pode permanecer.",
        });
      }

      const { error } = await supabase.from("activities").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      // ["activities"] já é prefixo das variantes por orgId — invalidar só ele.
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["lead-activities"] });
      queryClient.invalidateQueries({ queryKey: ['supervisao', 'operacao-reps'] });
      toast({ title: "Atividade excluída" });
    },
    onError: (error) => {
      console.error("Error deleting activity:", error);
      toast({
        title: "Erro ao excluir atividade",
        description: "Ocorreu um erro ao excluir a atividade. Tente novamente.",
        variant: "destructive",
      });
    },
  });
};
